// Add an issue that WordPress knows nothing about.
//
// Two API calls presented as one gesture, shown as two labelled steps so a
// failure says which half failed — they fail for different reasons and need
// different fixes:
//
//   upload  → writes the PDF to EFS, where the renderer already looks
//   ingest  → creates the edition row and queues the render
//
// Orphan adoption reuses this with `existingSku` set: the PDF is already on
// EFS, so the upload step is skipped entirely.

import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../api/client'
import { issueActionError } from './usePublishIssue'

const PUB_NAMES: Record<string, string> = {
  rc_car_action: 'RC Car Action',
  flight_journal: 'Flight Journal',
  model_airplane_news: 'Model Airplane News',
  diecast: 'Die Cast X',
}

type Step = 'idle' | 'uploading' | 'ingesting' | 'done'

export function AddIssueDialog({
  open, onClose, existingSku, existingPub,
}: {
  open: boolean
  onClose: () => void
  /** Adopting an orphan: its PDF is already on EFS, so no file is needed. */
  existingSku?: string
  existingPub?: string
}) {
  const qc = useQueryClient()
  const [pub, setPub] = useState(existingPub ?? 'flight_journal')
  const [sku, setSku] = useState(existingSku ?? '')
  const [title, setTitle] = useState('')
  const [issueDate, setIssueDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  )
  const [file, setFile] = useState<File | null>(null)
  const [step, setStep] = useState<Step>('idle')
  const [error, setError] = useState<string | null>(null)

  const adopting = Boolean(existingSku)

  useEffect(() => {
    if (!open) return
    setPub(existingPub ?? 'flight_journal')
    setSku(existingSku ?? '')
    setTitle('')
    setFile(null)
    setStep('idle')
    setError(null)
  }, [open, existingSku, existingPub])

  // The SKU is what names the file on EFS and what the renderer matches on,
  // so it is pre-filled from the filename but stays editable.
  const onPickFile = (f: File | null) => {
    setFile(f)
    if (f && !sku) setSku(f.name.replace(/\.pdf$/i, ''))
  }

  const run = useMutation({
    mutationFn: async () => {
      setError(null)
      if (!adopting) {
        if (!file) throw new ApiError(400, { detail: { message: 'Choose a PDF first.' } })
        setStep('uploading')
        const fd = new FormData()
        fd.append('publication_slug', pub)
        fd.append('sku', sku.trim())
        fd.append('year', String(new Date(issueDate).getUTCFullYear()))
        fd.append('file', file)
        await api.postForm('/api/lab/issues/upload', fd)
      }
      setStep('ingesting')
      return api.post('/api/lab/issues/ingest', {
        publication_slug: pub,
        sku: sku.trim(),
        title: title.trim(),
        post_date: issueDate,
      })
    },
    onSuccess: () => {
      setStep('done')
      qc.invalidateQueries({ queryKey: ['lab', 'issues'] })
      onClose()
    },
    onError: (e: unknown) => {
      setError(issueActionError(e))
      setStep('idle')
    },
  })

  if (!open) return null

  const busy = run.isPending
  const canSubmit = sku.trim() && title.trim() && (adopting || file)

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-ink-700 border border-amber-glow/30 shadow-glow max-w-[560px] w-full p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-[20px] text-paper">
          {adopting ? `Ingest ${existingSku}` : 'Add an issue'}
        </h2>
        <p className="font-mono text-[11px] text-paper-trace leading-[1.6]">
          {adopting
            ? 'This PDF is already on EFS. Ingesting creates its issue row and queues the render.'
            : 'The PDF is written to EFS, then an issue row is created and the render queued. The issue starts as a draft — publish it once the pages are there.'}
        </p>

        {!adopting && (
          <label className="block space-y-1">
            <span className="label">Issue PDF</span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              disabled={busy}
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              className="block w-full font-mono text-[12px] text-paper
                file:mr-3 file:cursor-pointer file:border file:border-amber-glow/40
                file:bg-ink-900 file:px-3 file:py-1.5 file:font-mono file:text-[11px]
                file:uppercase file:tracking-label file:text-amber-glow
                hover:file:bg-ink-600 disabled:opacity-40"
            />
            {file && (
              <span className="font-mono text-[10px] text-paper-trace">
                {(file.size / 1048576).toFixed(1)} MB
              </span>
            )}
          </label>
        )}

        <label className="block space-y-1">
          <span className="label">Publication</span>
          <select
            value={pub}
            disabled={busy || adopting}
            onChange={(e) => setPub(e.target.value)}
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 font-mono text-[12px] text-paper disabled:opacity-60"
          >
            {Object.entries(PUB_NAMES).map(([slug, name]) => (
              <option key={slug} value={slug}>{name}</option>
            ))}
          </select>
        </label>

        <label className="block space-y-1">
          <span className="label">SKU</span>
          <input
            value={sku}
            disabled={busy || adopting}
            onChange={(e) => setSku(e.target.value)}
            placeholder="FLJ112026 or FLJ-Winter-2026"
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 font-mono text-[12px] text-paper disabled:opacity-60"
          />
          <span className="font-mono text-[10px] text-paper-trace">
            Names the file on EFS and is how the renderer finds it. Use
            PREFIX+MMYYYY or PREFIX-Name-YYYY.
          </span>
        </label>

        <label className="block space-y-1">
          <span className="label">Title (members see this)</span>
          <input
            value={title}
            disabled={busy}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="November 2026"
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 font-mono text-[12px] text-paper"
          />
        </label>

        <label className="block space-y-1">
          <span className="label">Issue date</span>
          <input
            type="date"
            value={issueDate}
            disabled={busy}
            onChange={(e) => setIssueDate(e.target.value)}
            className="bg-ink-900 border border-ink-500 px-3 py-2 font-mono text-[12px] text-paper"
          />
        </label>

        {busy && (
          <div className="font-mono text-[11px] text-amber-glow">
            {step === 'uploading'
              ? 'Uploading the PDF… large issues take a while, don’t close this.'
              : 'Creating the issue and queueing the render…'}
          </div>
        )}
        {error && (
          <div className="border border-radar-red/50 text-radar-red font-mono text-[11px] p-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="font-mono text-[11px] tracking-label uppercase border border-paper-trace text-paper-trace px-3 py-1.5 hover:text-paper disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => run.mutate()}
            disabled={busy || !canSubmit}
            className="font-mono text-[11px] tracking-label uppercase bg-amber-glow text-ink-900 px-3 py-1.5 disabled:opacity-30"
          >
            {busy ? 'Working…' : adopting ? 'Ingest' : 'Upload and ingest'}
          </button>
        </div>
      </div>
    </div>
  )
}
